import { router, usePage } from '@inertiajs/vue3';
import { useToast } from '@laravel-inertia-toast/vue';
import { onUnmounted, ref } from 'vue';
import { useI18n } from '@/composables/useI18n';
import { createEcho, destroyEcho } from '@/echo';
import { playNotificationSound } from '@/lib/sound';
import type { AppNotificationItem } from '@/types/global';

/**
 * Shared notification store + real-time wiring (Phase 8).
 *
 * - When broadcasting (Reverb) is enabled, new notifications are pushed over
 *   the private `App.Models.User.{id}` channel and trigger a badge refresh,
 *   a toast and (for new messages) a sound.
 * - When `BROADCAST_POLLING_ENABLED` is set (websockets not reliable yet),
 *   the bell polls `/notifications/unread` every 30 s instead of opening a
 *   websocket. A polling safety net also kicks in when Reverb is not
 *   configured or the websocket connection errors out.
 *
 * Module-level state so the bell (header + sidebar) and any page share the
 * same unread counter.
 */

const unreadCount = ref(0);
const items = ref<AppNotificationItem[]>([]);

let pollingInterval: ReturnType<typeof setInterval> | null = null;
let realtimeActive = false;

/**
 * Sous-ensemble de la connexion pusher-js accessible depuis Echo, utilisé
 * pour détecter un websocket qui ne passe pas (fallback polling).
 */
interface EchoConnection {
    connector?: {
        pusher?: {
            connection?: {
                bind(event: string, callback: () => void): void;
            };
        };
    };
}

export function useNotifications() {
    const { __ } = useI18n();

    function ensurePolling(): void {
        if (!pollingInterval) {
            pollingInterval = setInterval(() => {
                void refresh();
            }, 30_000);
        }
    }

    async function refresh(): Promise<void> {
        try {
            const response = await fetch('/notifications/unread', {
                headers: { Accept: 'application/json' },
            });

            if (!response.ok) {
                return;
            }

            const data = (await response.json()) as {
                unread_count: number;
                items: AppNotificationItem[];
            };

            unreadCount.value = data.unread_count;
            items.value = data.items;
        } catch {
            // Network error — keep the previous state.
        }
    }

    function titleFor(type: string, data: AppNotificationItem['data']): string {
        switch (type) {
            case 'WhatsAppInstanceBanned':
                return __('notifications.instance_banned_title', {
                    name: data.instance_name ?? '',
                });
            case 'WhatsAppInstanceDisconnected':
                return __('notifications.instance_disconnected_title', {
                    name: data.instance_name ?? '',
                });
            case 'WhatsAppBanLifted':
                return __('notifications.ban_lifted_title', {
                    name: data.instance_name ?? '',
                });
            case 'CampaignPausedByAntiBan':
                return __('notifications.campaign_paused_title', {
                    name: data.campaign_name ?? '',
                });
            case 'PaymentConfirmed':
                return data.title ?? __('notifications.default');
            case 'PaymentFailed':
            case 'QuotaAlmostReached':
                return data.title ?? __('notifications.default');
            case 'NewIncomingMessage':
                return __('notifications.new_message_title', {
                    name: data.sender_name || data.instance_name || '',
                });
            case 'CampaignCompleted':
                return __('notifications.campaign_completed_title', {
                    name: data.campaign_name ?? '',
                });
            case 'ContactImportFinished':
                return __('notifications.import_finished_title');
            default:
                return __('notifications.default');
        }
    }

    function messageFor(
        type: string,
        data: AppNotificationItem['data'],
    ): string {
        switch (type) {
            case 'WhatsAppInstanceBanned':
                return data.banned_until
                    ? __('notifications.instance_banned_until', {
                          date: data.banned_until,
                      })
                    : __('notifications.instance_banned');
            case 'WhatsAppInstanceDisconnected':
                return data.reason && data.reason !== 'unknown'
                    ? __('notifications.instance_disconnected_reason', {
                          reason: data.reason,
                      })
                    : __('notifications.instance_disconnected');
            case 'WhatsAppBanLifted':
                return __('notifications.ban_lifted');
            case 'CampaignPausedByAntiBan':
                return __('notifications.campaign_paused');
            case 'PaymentConfirmed':
                return data.message ?? '';
            case 'PaymentFailed':
            case 'QuotaAlmostReached':
                return data.message ?? '';
            case 'NewIncomingMessage':
                return data.content
                    ? String(data.content).slice(0, 140)
                    : __('notifications.new_message_body');
            case 'CampaignCompleted':
                return __('notifications.campaign_completed_message', {
                    sent: String(data.messages_sent ?? 0),
                    failed: String(data.messages_failed ?? 0),
                });
            case 'ContactImportFinished':
                return __('notifications.import_finished_message', {
                    imported: String(data.imported ?? 0),
                    skipped: String(data.skipped ?? 0),
                });
            default:
                return '';
        }
    }

    function handleRealtimeNotification(notification: {
        type: string;
        data?: AppNotificationItem['data'];
    }): void {
        const type =
            String(notification.type ?? '')
                .split('\\')
                .pop() ?? '';

        // Refresh the bell from the server, then surface the event.
        void refresh().then(() => {
            if (type === 'NewIncomingMessage') {
                playNotificationSound();
            }

            const toast = useToast();
            const title = titleFor(type, notification.data ?? {});
            const message = messageFor(type, notification.data ?? {});

            toast.info(message || title, { title });
        });
    }

    function startRealtime(): void {
        const page = usePage();
        const broadcast = page.props.broadcast as
            | {
                  enabled?: boolean;
                  polling?: boolean;
                  key?: string;
                  host?: string;
                  port?: number;
                  scheme?: string;
                  userId?: number;
              }
            | undefined;

        // Mode polling forcé (websockets pas encore fiables en production) :
        // on rafraîchit la cloche à intervalle régulier, sans websocket.
        if (broadcast?.polling) {
            ensurePolling();

            return;
        }

        if (broadcast?.enabled && broadcast.key && broadcast.userId) {
            if (realtimeActive) {
                // Filet de sécurité déjà actif : on garde aussi le polling.
                ensurePolling();

                return;
            }

            const echo = createEcho({
                key: broadcast.key,
                host: broadcast.host ?? '127.0.0.1',
                port: broadcast.port ?? 80,
                scheme: (broadcast.scheme === 'https' ? 'https' : 'http') as
                    'http' | 'https',
            });

            echo.private(`App.Models.User.${broadcast.userId}`).notification(
                (notification: {
                    type: string;
                    data?: AppNotificationItem['data'];
                }) => {
                    handleRealtimeNotification(notification);
                },
            );

            realtimeActive = true;

            // Filet de sécurité : si le websocket ne passe pas (Reverb down,
            // certificat TLS invalide…), on bascule sur le polling.
            const connection = (echo as unknown as EchoConnection).connector
                ?.pusher?.connection;
            connection?.bind?.('error', ensurePolling);
            connection?.bind?.('disconnected', ensurePolling);

            return;
        }

        // Reverb non configuré : polling silencieux.
        ensurePolling();
    }

    function stopRealtime(): void {
        if (pollingInterval) {
            clearInterval(pollingInterval);
            pollingInterval = null;
        }

        if (realtimeActive) {
            destroyEcho();
            realtimeActive = false;
        }
    }

    /**
     * Call from a persistent layout: starts real-time delivery once.
     */
    function setup(): void {
        startRealtime();

        onUnmounted(() => {
            stopRealtime();
        });
    }

    async function openNotification(
        notification: AppNotificationItem,
    ): Promise<void> {
        if (!notification.data.url) {
            return;
        }

        await fetch(`/notifications/${notification.id}/read`, {
            method: 'POST',
            headers: {
                'X-CSRF-TOKEN': window.csrfToken,
                Accept: 'application/json',
            },
        });

        unreadCount.value = Math.max(0, unreadCount.value - 1);
        items.value = items.value.filter((item) => item.id !== notification.id);

        router.visit(notification.data.url);
    }

    async function markAllRead(): Promise<void> {
        await fetch('/notifications/read-all', {
            method: 'POST',
            headers: {
                'X-CSRF-TOKEN': window.csrfToken,
                Accept: 'application/json',
            },
        });

        unreadCount.value = 0;
        items.value = [];
    }

    return {
        unreadCount,
        items,
        refresh,
        titleFor,
        messageFor,
        setup,
        openNotification,
        markAllRead,
    };
}
